// apps/widget/src/screens/qr-handoff.screen.tsx
//
// Desktop → phone. Offered always, not only when no camera is found: phone
// cameras are better, and asking is cheaper than detecting.
//
// The moment that makes this feel finished rather than uncertain is the
// thumbnail appearing here the instant the phone captures — before analysis
// has even started.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { captureFromUrl } from '../lib/remote-capture';
import { subscribeToScan } from '../lib/sse';
import { t } from '../lib/i18n';
import { useScan } from '../state';

type Phase = 'waiting' | 'connected' | 'captured' | 'expired';

export function QrHandoffScreen() {
  const { state, dispatch, go } = useScan();
  const [phase, setPhase] = useState<Phase>('waiting');
  const [session, setSession] = useState(state.session);
  /** The phone's photo, being brought here so the scan plays over it. */
  const photo = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    void (async () => {
      if (session) return;
      const created = await api.createSession(state.intake.declaredAge, { retainPhoto: state.retainPhoto });
      dispatch({ type: 'session', session: created });
      setSession(created);
    })();
  }, []);

  useEffect(() => {
    if (!session) return;

    const key = new URLSearchParams(location.search).get('key') ?? location.pathname.split('/')[2] ?? '';

    const stream = subscribeToScan(session.sessionId, key, {
      onEvent: (event) => {
        if (event.type === 'connected') setPhase('connected');
        if (event.type === 'uploaded') {
          setPhase('captured');
          if (event.thumbnailUrl) {
            dispatch({ type: 'thumbnail', url: event.thumbnailUrl });
            // The phone only takes the photo; the scan plays here, over the
            // photo itself, exactly as it does after a webcam capture.
            photo.current = captureFromUrl(event.thumbnailUrl).then((capture) => {
              if (capture) dispatch({ type: 'captured', capture });
            });
          }
        }
        // Analysis is running on the worker; the desktop follows it through to
        // the report without the visitor touching anything — once the photo
        // is here, or a few seconds have passed without it.
        // Or, if analysis was quick enough to finish before this screen heard
        // about it, straight on — the analysing screen catches up and shows the
        // report.
        if ((event.type === 'analyzing' || event.type === 'complete') && event.scanId) {
          const scanId = event.scanId;
          void Promise.race([photo.current ?? Promise.resolve(), wait(PHOTO_WITHIN_MS)]).then(() =>
            dispatch({ type: 'scanning', scanId }),
          );
        }
      },
      onFailed: (error) => dispatch({ type: 'error', error }),
    });

    const expiry = setTimeout(
      () => setPhase('expired'),
      new Date(session.expiresAt).getTime() - Date.now(),
    );

    return () => {
      stream.close();
      clearTimeout(expiry);
    };
  }, [session, dispatch]);

  if (!session) {
    return <div className="gs-skeleton" style={{ height: 320 }} aria-busy="true" />;
  }

  return (
    <div className="gs-stack gs-qr">
      <h1 className="gs-title">{t('qr.title')}</h1>
      <p className="gs-body">{t('qr.subtitle')}</p>

      <div className="gs-qr-frame" data-state={phase}>
        {phase === 'captured' && state.thumbnailUrl ? (
          <img src={state.thumbnailUrl} alt="" className="gs-qr-thumb" />
        ) : (
          <img src={session.qrDataUrl} alt={t('qr.title')} width={240} height={240} />
        )}
      </div>

      {/* Status is a live region: a visitor watching the code needs to know the
          moment their phone lands, without looking away. Three steps rather
          than one line, so "the phone has it" never reads as "still waiting". */}
      {phase === 'expired' ? (
        <p className="gs-status" role="status" aria-live="polite">
          {t('qr.expired')}
        </p>
      ) : (
        <>
          <ol className="gs-qr-steps" aria-hidden="true">
            {(['scan', 'photo', 'result'] as const).map((step, index) => {
              const current = phase === 'waiting' ? 0 : phase === 'connected' ? 1 : 2;
              return (
                <li key={step} data-state={index < current ? 'done' : index === current ? 'current' : 'todo'}>
                  <span className="gs-qr-steps__dot">{index < current ? '✓' : index + 1}</span>
                  <span className="gs-qr-steps__label">{t(`qr.step.${step}`)}</span>
                </li>
              );
            })}
          </ol>
          <p className="gs-status" data-phase={phase} role="status" aria-live="polite">
            {phase === 'waiting' && (
              <>
                <span className="gs-status__pulse" aria-hidden="true" />
                {t('qr.waitingHint')}
              </>
            )}
            {phase === 'connected' && (
              <>
                <strong>✓ {t('qr.connected')}.</strong> {t('qr.connectedHint')}
              </>
            )}
            {phase === 'captured' && (
              <>
                <strong>✓ {t('qr.captured')}.</strong> {t('qr.capturedHint')}
              </>
            )}
          </p>
        </>
      )}

      {phase === 'expired' && (
        <button
          className="gs-btn gs-btn--primary"
          onClick={() => {
            setSession(null);
            setPhase('waiting');
          }}
        >
          {t('qr.newCode')}
        </button>
      )}

      <button className="gs-btn gs-btn--ghost" onClick={() => go('capture')}>
        {t('common.back')}
      </button>
    </div>
  );
}

/** How long the scan waits for the phone's photo before playing without it. */
const PHOTO_WITHIN_MS = 5_000;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
